import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { format, isToday } from "date-fns";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import CalendarDayColumn from "./CalendarDayColumn";
import CopyHoursDialog from "./CopyHoursDialog";
import IntervalPopover, { IntervalEdit } from "./IntervalPopover";
import { useSchedule } from "../SchedulerProvider";
import Rule from "../store/Rule";
import {
  DEFAULT_SCROLL_HOUR,
  HOUR_HEIGHT,
  HOURS,
  formatHourLabel,
  formatMinutes,
  minutesNow,
  minutesToOffset,
} from "../utils/calendar";

type Props = {
  // Ordered list of dates to render as columns (7 for the week view, 1 for the
  // day view).
  dates: Date[];
};

const GUTTER_WIDTH = 56;
const COLUMN_HEIGHT = 24 * HOUR_HEIGHT;
const SCROLL_VIEWPORT_HEIGHT = 620;

const CalendarTimeGrid: React.FC<Props> = observer(({ dates }) => {
  const { classes, cx } = useStyles();
  const schedule = useSchedule();
  const scrollRef = useRef<HTMLDivElement>(null);

  const showsToday = dates.some((date) => isToday(date));
  const [nowMinutes, setNowMinutes] = useState(minutesNow);
  const [editing, setEditing] = useState<IntervalEdit | null>(null);
  const [menu, setMenu] = useState<{
    anchorEl: HTMLElement;
    date: Date;
    rule: Rule;
  } | null>(null);
  const [copySource, setCopySource] = useState<{
    rule: Rule;
    label: string;
  } | null>(null);
  // ISO of the day column a block is being dragged over (for cross-day moves).
  const [dropDate, setDropDate] = useState<string | null>(null);

  const clearDay = (rule: Rule) => {
    schedule.pushHistory();
    rule.removeIntervals();
    setMenu(null);
  };

  // Keep the "now" line current without re-rendering the whole tree constantly.
  useEffect(() => {
    if (!showsToday) return;
    const id = window.setInterval(() => setNowMinutes(minutesNow()), 60_000);
    return () => window.clearInterval(id);
  }, [showsToday]);

  useEffect(() => {
    // Open scrolled to the current time when today is visible, otherwise to
    // working hours rather than midnight.
    if (!scrollRef.current) return;
    const target = showsToday
      ? minutesToOffset(nowMinutes) - SCROLL_VIEWPORT_HEIGHT / 2
      : DEFAULT_SCROLL_HOUR * HOUR_HEIGHT;
    scrollRef.current.scrollTop = Math.max(0, target);
    // Only on mount / when the visible dates change, not every minute.
  }, [dates[0]?.toISOString(), dates.length, showsToday]);

  const single = dates.length === 1;

  return (
    <Box className={classes.root}>
      <div className={classes.headerRow}>
        <div className={classes.gutterSpacer} />
        {dates.map((date) => {
          const { rule, isOverride } = schedule.effectiveRuleForDate(date);
          const today = isToday(date);
          return (
            <div
              key={date.toISOString()}
              className={cx(classes.dayHeader, today && classes.dayHeaderToday)}
            >
              <Tooltip title="Day options">
                <IconButton
                  size="small"
                  className={classes.kebab}
                  onClick={(event) =>
                    setMenu({ anchorEl: event.currentTarget, date, rule })
                  }
                >
                  <MoreVertIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Typography className={classes.dayName}>
                {format(date, single ? "EEEE" : "EEE").toUpperCase()}
              </Typography>
              <Typography className={classes.dayNumber}>
                {format(date, single ? "MMM d" : "d")}
              </Typography>
              {isOverride ? (
                <Chip
                  size="small"
                  label="Exception"
                  icon={<EventRepeatIcon />}
                  onDelete={() =>
                    schedule.effectiveRuleForDate(date).rule.remove()
                  }
                  deleteIcon={<CloseIcon />}
                  className={classes.exceptionChip}
                />
              ) : (
                <Tooltip title="Set different hours for this date only">
                  <button
                    type="button"
                    className={classes.addException}
                    onClick={() => schedule.ensureOverrideForDate(date)}
                  >
                    + Exception
                  </button>
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>

      <div
        ref={scrollRef}
        className={classes.scrollBody}
        style={{ maxHeight: SCROLL_VIEWPORT_HEIGHT }}
      >
        <div className={classes.grid} style={{ height: COLUMN_HEIGHT }}>
          <div className={classes.gutter}>
            {HOURS.map((hour) => (
              <span
                key={hour}
                className={classes.hourLabel}
                style={{ top: hour * HOUR_HEIGHT }}
              >
                {hour === 0 ? "" : formatHourLabel(hour)}
              </span>
            ))}
          </div>
          {dates.map((date) => {
            const { rule, isOverride } = schedule.effectiveRuleForDate(date);
            return (
              <CalendarDayColumn
                key={date.toISOString()}
                date={date}
                rule={rule}
                isOverride={isOverride}
                isToday={isToday(date)}
                onEditInterval={setEditing}
                onBeforeMutate={schedule.pushHistory}
                isDropTarget={dropDate === date.toISOString()}
                onHoverDate={setDropDate}
              />
            );
          })}

          {showsToday && (
            <>
              <span
                className={classes.nowLabel}
                style={{ top: minutesToOffset(nowMinutes) }}
              >
                {formatMinutes(nowMinutes)}
              </span>
              <div
                className={classes.nowLine}
                style={{ top: minutesToOffset(nowMinutes) }}
              />
            </>
          )}
        </div>
      </div>

      <Box className={classes.legend}>
        <span className={cx(classes.swatch, classes.swatchRecurring)} />
        <Typography className={classes.legendText}>Weekly hours</Typography>
        <span className={cx(classes.swatch, classes.swatchOverride)} />
        <Typography className={classes.legendText}>Date exception</Typography>
        <Typography className={classes.legendHint}>
          Drag to add · drag edges to resize · drag a block to move it (across
          days too) · click a block to edit
        </Typography>
      </Box>

      <IntervalPopover
        editing={editing}
        onClose={() => setEditing(null)}
        onBeforeMutate={schedule.pushHistory}
        onMakeException={(date) => {
          schedule.pushHistory();
          schedule.ensureOverrideForDate(date);
          setEditing(null);
        }}
      />

      <Menu
        open={!!menu}
        anchorEl={menu?.anchorEl ?? null}
        onClose={() => setMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (menu)
              setCopySource({
                rule: menu.rule,
                label: format(menu.date, "EEEE"),
              });
            setMenu(null);
          }}
        >
          Copy hours to…
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) schedule.mergeOverlaps(menu.rule);
            setMenu(null);
          }}
        >
          Merge overlapping
        </MenuItem>
        <MenuItem onClick={() => menu && clearDay(menu.rule)}>
          Clear day
        </MenuItem>
      </Menu>

      <CopyHoursDialog
        open={!!copySource}
        onClose={() => setCopySource(null)}
        sourceRule={copySource?.rule ?? null}
        sourceLabel={copySource?.label ?? ""}
      />
    </Box>
  );
});

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #DEE6F0",
    borderRadius: 8,
    overflow: "hidden",
  },
  headerRow: {
    display: "flex",
    borderBottom: "1px solid #DEE6F0",
  },
  gutterSpacer: {
    width: GUTTER_WIDTH,
    flexShrink: 0,
  },
  dayHeader: {
    position: "relative",
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "8px 4px",
    borderLeft: "1px solid #DEE6F0",
  },
  kebab: {
    position: "absolute",
    top: 2,
    right: 2,
    padding: 2,
    color: "#9AA3B2",
    fontSize: 16,
  },
  dayHeaderToday: {
    backgroundColor: "rgba(61,109,240,0.06)",
  },
  dayName: {
    fontSize: 11,
    fontWeight: 700,
    color: "#7D8797",
    letterSpacing: 0.5,
  },
  dayNumber: {
    fontSize: 18,
    fontWeight: 700,
    color: "#363E45",
    lineHeight: 1.2,
  },
  exceptionChip: {
    marginTop: 4,
    height: 20,
    backgroundColor: "#E7F6EC",
    color: "#1C5C36",
    "& .MuiChip-label": { fontSize: 10, fontWeight: 700 },
    "& .MuiChip-icon": { fontSize: 12, color: "#2E9E5B" },
    "& .MuiChip-deleteIcon": { fontSize: 12, color: "#1C5C36" },
  },
  addException: {
    marginTop: 4,
    padding: "1px 6px",
    fontSize: 10,
    fontWeight: 700,
    color: "#3D6DF0",
    background: "none",
    border: "1px solid transparent",
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": { borderColor: "#3D6DF0" },
  },
  scrollBody: {
    overflowY: "auto",
    overflowX: "hidden",
  },
  grid: {
    display: "flex",
    position: "relative",
  },
  gutter: {
    width: GUTTER_WIDTH,
    flexShrink: 0,
    position: "relative",
  },
  hourLabel: {
    position: "absolute",
    right: 6,
    fontSize: 10,
    fontWeight: 600,
    color: "#9AA3B2",
    transform: "translateY(-6px)",
  },
  nowLine: {
    position: "absolute",
    left: GUTTER_WIDTH,
    right: 0,
    height: 2,
    backgroundColor: "#E5484D",
    pointerEvents: "none",
    zIndex: 2,
    "&::before": {
      content: '""',
      position: "absolute",
      left: -4,
      top: -3,
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#E5484D",
    },
  },
  nowLabel: {
    position: "absolute",
    left: 0,
    width: GUTTER_WIDTH - 4,
    textAlign: "right",
    transform: "translateY(-6px)",
    fontSize: 10,
    fontWeight: 700,
    color: "#E5484D",
    pointerEvents: "none",
    zIndex: 2,
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderTop: "1px solid #DEE6F0",
    flexWrap: "wrap",
  },
  swatch: {
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    borderStyle: "solid",
    borderWidth: 1,
  },
  swatchRecurring: {
    backgroundColor: "#E8EEFF",
    borderColor: "#3D6DF0",
  },
  swatchOverride: {
    backgroundColor: "#E7F6EC",
    borderColor: "#2E9E5B",
    marginLeft: 8,
  },
  legendText: {
    fontSize: 12,
    color: "#4C515A",
    fontWeight: 600,
  },
  legendHint: {
    fontSize: 12,
    color: "#9AA3B2",
    marginLeft: "auto",
  },
}));

export default CalendarTimeGrid;
